import { useMemo, useRef, useEffect, useCallback, useState, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import { Milestone as MilestoneIcon, ChevronDown, ChevronRight, CheckSquare, Ban, CircleAlert, Users, Check, History, X } from 'lucide-react';
import type { Project, GridColumn, Task, GeranceHistory } from '../types';
import { useT } from '../i18n';
import { isPlanningOrExecutionStage, getClosedColumnIds, getBlockedColumnIds } from '../utils/stages';
import { getProjectMilestones, getMilestoneProgress, getMilestoneState } from '../utils/milestoneProgress';
import { parseYMD, getUrgency, getUrgencyAt, type Urgency } from '../utils/echeances';
import { fmtShortDate } from '../utils/date';
import { buildMonthRange, dateOffsetPx, monthKey, packLanes, type MonthSlot } from '../utils/monthGrid';
import { projectDisplayCode } from '../utils/identifiers';
import { projectIsStrategic, projectRcajoScore } from '../utils/rcajoScore';
import {
  toYMD, isoToLocalYMD, dayStartMs, dayEndMs, addDays, daysBetween,
  stageLabelAt, isPlanningOrExecutionLabel, milestonesAt, planCheckpoints,
  clusterEvents, CHIP_ICON_OFFSET, type TimelineEvent, type EventCluster,
} from '../utils/geranceHistory';
import type { RawMilestone } from '../utils/milestoneProgress';
import { parseJSON } from './ProjectForm';
import { MilestoneDetailPanel } from './MilestoneDetailPanel';

function projectSponsor(p: Project): string {
  return parseJSON<any>(p.ownership, {}).sponsor || '';
}

function projectCollaborators(p: Project): string {
  const collabs = parseJSON<any>(p.resources_effort, {}).collaborators;
  return Array.isArray(collabs) ? collabs.map((c: any) => c.name).filter(Boolean).join(', ') : '';
}

// Portfolio-wide Gantt: one lane per Planning/Execution project, milestones
// drawn as labelled boxes spanning estimated_start → estimated_end (per the
// user's sketch), plus a marker for the project's own overall deadline.
// Boxes stay compact by default — each has an expand toggle that reveals the
// milestone's open tasks in a strip under the row, instead of ballooning
// every box to fit task details it usually doesn't need.
//
// Each row ends in an activity rail, a quiet band of its own under the bars:
// what happened on the project, on its day — meetings held (calendar events
// linked to it) and tasks moved to Done. Those are facts and never move; the
// bars above follow the plan. Events close together share one chip with
// counts; hovering a chip opens a card listing them.
//
// The legend's time slider replays the portfolio: pick a past day and every
// row is redrawn as it stood then — milestones with the dates they had at the
// time (from their date history), only the events up to that day, and a
// project that wasn't in Planning/Execution then greys out while keeping its
// place. Rows keep one height across the whole slider range (the tallest the
// plan ever was) so nothing jumps while sliding.

const MONTH_W = 330;
const LABEL_W = 225; // +25% over the previous 180px
const MONTHS_BEFORE = 6;
const MONTHS_AFTER = 8;
const MIN_BAR_W = 64;
const BAR_H = 34;
const LANE_GAP = 6;
const BARS_MIN_H = BAR_H + 2 * LANE_GAP; // one lane of bars
const RAIL_H = 28;                       // the activity rail under the bars
const FOLLOW_MARGIN = 96;                // keep the slider's day this far inside the visible track

// Month gridlines for the track background: a strong line at the start of
// each month plus three lighter week ticks (quarters of the month), so a
// bar's edge can be read as "week 1/2/3/4" at a glance instead of just
// "somewhere in June".
function weekTick(x: number): string {
  return `transparent ${x}px, var(--line) ${x}px, var(--line) ${x + 1}px, transparent ${x + 1}px,`;
}
const TRACK_BG = `repeating-linear-gradient(to right,
  var(--line-strong) 0, var(--line-strong) 1px, transparent 1px,
  ${weekTick(MONTH_W / 4)}
  ${weekTick(MONTH_W / 2)}
  ${weekTick((MONTH_W / 4) * 3)}
  transparent ${MONTH_W}px)`;
// The rail keeps only the month lines: the week ticks are for reading bar
// edges, and on the rail they'd only compete with the chips.
const RAIL_BG = `repeating-linear-gradient(to right,
  var(--line-strong) 0, var(--line-strong) 1px, transparent 1px, transparent ${MONTH_W}px)`;

const MONTH_KEYS = [
  'calendar.month.jan', 'calendar.month.feb', 'calendar.month.mar', 'calendar.month.apr',
  'calendar.month.may', 'calendar.month.jun', 'calendar.month.jul', 'calendar.month.aug',
  'calendar.month.sep', 'calendar.month.oct', 'calendar.month.nov', 'calendar.month.dec',
];

function goToProject(id: number) {
  window.location.hash = `project?id=${id}`;
}

function goToTask(id: number) {
  window.location.hash = `tasks?task=${id}`;
}

interface RowMilestone {
  id: string;
  name: string;
  state: 'done' | 'active' | 'pending';
  // RAG status of the active milestone, from its own due date (getUrgency)
  // — null for done/pending bars, which aren't colored by urgency.
  urgency: Urgency | null;
  left: number;
  width: number;
  start: string;
  end: string;
}

/** An event with the instant it happened, for the slider's cutoff. */
type DatedEvent = TimelineEvent & { at: number };

interface OpenCard {
  projectId: number;
  cluster: EventCluster;
  rect: DOMRect;
}

const CARD_W = 300;
const CARD_MAX_ITEMS = 8;

// The card a chip opens: every event it stands for, meetings then tasks,
// each clickable (a task opens the task, a meeting the project). Rendered in
// a portal at the chip, so the timeline's scroll box never clips it.
function EventCard({ card, onEnter, onLeave, t }: {
  card: OpenCard;
  onEnter: () => void;
  onLeave: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const { cluster, rect, projectId } = card;
  const all = [...cluster.meetings, ...cluster.tasks].map(e => e.date).sort();
  const first = all[0];
  const last = all[all.length - 1];
  const left = Math.min(Math.max(8, rect.left + rect.width / 2 - CARD_W / 2), window.innerWidth - CARD_W - 8);
  const below = rect.bottom + 260 < window.innerHeight;
  const style: React.CSSProperties = below
    ? { left, top: rect.bottom + 8, width: CARD_W }
    : { left, bottom: window.innerHeight - rect.top + 8, width: CARD_W };

  const section = (kind: 'meeting' | 'task', events: TimelineEvent[]) => {
    if (events.length === 0) return null;
    const shown = [...events].sort((a, b) => a.date.localeCompare(b.date)).slice(0, CARD_MAX_ITEMS);
    return (
      <div className={`cal-card-sec is-${kind}`}>
        <div className="cal-card-sec-title">
          {kind === 'meeting' ? <Users size={12} /> : <Check size={12} strokeWidth={3} />}
          {t(kind === 'meeting' ? 'gerancemeta.cardMeetings' : 'gerancemeta.cardTasksDone', { n: events.length })}
        </div>
        {shown.map((ev, i) => {
          const target = kind === 'task' ? ev.id : projectId;
          return (
            <button
              key={`${ev.id ?? 'x'}-${ev.date}-${i}`}
              type="button"
              className="cal-card-item"
              disabled={target == null}
              onClick={() => { if (kind === 'task' && ev.id != null) goToTask(ev.id); else goToProject(projectId); }}
            >
              <span className="cal-card-date">{fmtShortDate(ev.date)}</span>
              <span className="cal-card-text">
                <span className="cal-card-title">{ev.title || '—'}</span>
                {ev.by && <span className="cal-card-by">{ev.by}</span>}
              </span>
            </button>
          );
        })}
        {events.length > shown.length && (
          <div className="cal-card-more">{t('gerancemeta.cardMore', { n: events.length - shown.length })}</div>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="cal-card" style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="cal-card-head">{first === last ? fmtShortDate(first) : `${fmtShortDate(first)} – ${fmtShortDate(last)}`}</div>
      {section('meeting', cluster.meetings)}
      {section('task', cluster.tasks)}
    </div>,
    document.body,
  );
}

export function GeranceCalendarView({ projects, pipelineColumns, kanbanColumns, tasks, projectDeadlines, history }: {
  projects: Project[];
  pipelineColumns: GridColumn[];
  kanbanColumns: GridColumn[];
  tasks: Task[];
  projectDeadlines: Map<number, string>;
  history: GeranceHistory | null;
}) {
  const { t } = useT();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Time slider: the day the calendar is drawn "as of" — null = today (live).
  // The slider and its label follow `asOf` at once; the calendar redraws from
  // the deferred copy, so dragging stays fluid while the rows catch up.
  const [asOf, setAsOf] = useState<string | null>(null);
  const shownAsOf = useDeferredValue(asOf);
  // Row-level toggle: the label-column button, always the current/active
  // milestone's tasks + the project details (domain/collaborators/RCAJO).
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  // Per-bar toggle for done/pending milestones — each one independent, keyed
  // "projectId:milestoneId" so several can be open across different projects
  // (or different milestones of the same project) at once.
  const [expandedMilestones, setExpandedMilestones] = useState<Set<string>>(new Set());
  // The milestone whose "!" overlay was clicked — drives the detail side panel.
  const [selectedMilestone, setSelectedMilestone] = useState<{ project: Project; bar: RowMilestone } | null>(null);
  // The activity chip whose card is open (hover, focus or tap).
  const [card, setCard] = useState<OpenCard | null>(null);
  const closeTimer = useRef<number | null>(null);
  // Done or archived: neither counts as an open task of a milestone.
  const doneColumnIds = useMemo(() => getClosedColumnIds(kanbanColumns), [kanbanColumns]);
  const blockedColumnIds = useMemo(() => getBlockedColumnIds(kanbanColumns), [kanbanColumns]);

  const toggleRow = useCallback((projectId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
      return next;
    });
  }, []);

  const toggleMilestone = useCallback((key: string) => {
    setExpandedMilestones(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current != null) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setCard(null), 140);
  }, [cancelClose]);
  const openCard = useCallback((projectId: number, cluster: EventCluster, el: HTMLElement) => {
    cancelClose();
    setCard({ projectId, cluster, rect: el.getBoundingClientRect() });
  }, [cancelClose]);
  useEffect(() => () => cancelClose(), [cancelClose]);

  const months = useMemo<MonthSlot[]>(() => buildMonthRange(MONTHS_BEFORE, MONTHS_AFTER), []);
  const totalWidth = months.length * MONTH_W;

  const today = new Date();
  const todayStr = toYMD(today);
  const todayLeft = dateOffsetPx(todayStr, months, MONTH_W);
  const todayMonthIdx = months.findIndex(m => m.year === today.getFullYear() && m.month === today.getMonth());
  // The slider runs from the first visible month to today, one step per day.
  const sliderStart = `${months[0].year}-${String(months[0].month + 1).padStart(2, '0')}-01`;
  const sliderStartMs = dayStartMs(sliderStart);
  const sliderDays = Math.max(0, daysBetween(sliderStart, todayStr));
  const sliderValue = asOf ? Math.min(sliderDays, Math.max(0, daysBetween(sliderStart, asOf))) : sliderDays;
  const asOfMs = shownAsOf && shownAsOf < todayStr ? dayEndMs(shownAsOf) : null;
  const asOfLeft = asOfMs != null && shownAsOf ? dateOffsetPx(shownAsOf, months, MONTH_W) : null;

  // Milestones → positioned bars. Live: state from the plan (completed /
  // current / upcoming) and urgency from today. As of a past day: the plan's
  // status then isn't recorded, so the first milestone still due on that day
  // is drawn as the current one, earlier ones as done, later ones as upcoming,
  // urgency measured from that day.
  const buildBars = useCallback((milestones: RawMilestone[], refMs: number | null) => {
    const withEnd = milestones.filter(m => m.estimated_end);
    const progress = refMs == null ? getMilestoneProgress(milestones) : null;
    const refStart = refMs == null ? null : dayStartMs(toYMD(new Date(refMs)));
    let currentIdx = -1;
    if (refStart != null) {
      currentIdx = withEnd.findIndex(m => parseYMD(m.estimated_end) >= refStart);
      if (currentIdx === -1 && withEnd.length > 0) currentIdx = withEnd.length - 1;
    }
    const rawBars = withEnd.map((m, i) => {
      const startStr = m.estimated_start || m.estimated_end;
      const left = dateOffsetPx(startStr, months, MONTH_W) ?? 0;
      let right = dateOffsetPx(m.estimated_end, months, MONTH_W) ?? left;
      if (right < left) right = left;
      if (right - left < MIN_BAR_W) right = left + MIN_BAR_W;
      const state: RowMilestone['state'] = refStart == null
        ? getMilestoneState(m, milestones.indexOf(m), progress)
        : i < currentIdx ? 'done' : i === currentIdx ? 'active' : 'pending';
      const urgency = state !== 'active' ? null
        : refStart == null ? getUrgency(m.estimated_end) : getUrgencyAt(m.estimated_end, refStart);
      return { id: m.id, name: m.name, state, urgency, left, width: right - left, start: m.estimated_start, end: m.estimated_end };
    });
    const bars = packLanes(rawBars);
    const laneCount = bars.length > 0 ? Math.max(...bars.map(b => b.lane)) + 1 : 0;
    return { bars, laneCount };
  }, [months]);

  const statusLabel = useCallback((statusId: number) => pipelineColumns.find(c => c.id === statusId)?.label || '', [pipelineColumns]);

  // History indexed by project once, not on every slider step.
  const historyIndex = useMemo(() => {
    const statusChanges = new Map<number, GeranceHistory['statusChanges']>();
    const milestoneEvents = new Map<number, GeranceHistory['milestoneEvents']>();
    for (const c of history?.statusChanges ?? []) {
      if (!statusChanges.has(c.project_id)) statusChanges.set(c.project_id, []);
      statusChanges.get(c.project_id)!.push(c);
    }
    for (const e of history?.milestoneEvents ?? []) {
      if (!milestoneEvents.has(e.project_id)) milestoneEvents.set(e.project_id, []);
      milestoneEvents.get(e.project_id)!.push(e);
    }
    return { statusChanges, milestoneEvents };
  }, [history]);

  // Everything that happened on each project inside the visible window, up
  // to now: meetings held and tasks moved to a Done/archived column.
  const doneLabels = useMemo(
    () => new Set(kanbanColumns.filter(c => doneColumnIds.has(c.id)).map(c => (c.label || '').trim().toLowerCase())),
    [kanbanColumns, doneColumnIds]);
  const eventsByProject = useMemo(() => {
    const map = new Map<number, DatedEvent[]>();
    if (!history) return map;
    const now = Date.now();
    const add = (projectId: number, kind: TimelineEvent['kind'], iso: string, rest: Pick<TimelineEvent, 'id' | 'title' | 'by'>) => {
      const at = new Date(iso).getTime();
      if (Number.isNaN(at) || at > now || at < sliderStartMs) return;
      const date = isoToLocalYMD(iso);
      const x = dateOffsetPx(date, months, MONTH_W);
      if (x == null) return;
      if (!map.has(projectId)) map.set(projectId, []);
      map.get(projectId)!.push({ kind, date, x, at, ...rest });
    };
    for (const m of history.meetings) add(m.project_id, 'meeting', m.start_time, { id: m.id, title: m.subject, by: m.organizer_name });
    for (const c of history.taskStatus) {
      if (!doneLabels.has((c.new_column || '').trim().toLowerCase())) continue;
      add(c.project_id, 'task', c.at, { id: c.task_exists ? c.task_id : null, title: c.title || '', by: c.by });
    }
    return map;
  }, [history, months, sliderStartMs, doneLabels]);

  // The list itself never changes with the slider: same projects, same order.
  // On today each row is as tall as its plan is now; once the slider is in the
  // past, each row takes the tallest its plan ever was within the slider's
  // range, so the list settles once and never jumps while sliding.
  const baseRows = useMemo(() => {
    const now = Date.now();
    const eligible = projects.filter(p => isPlanningOrExecutionStage(p.status_id, pipelineColumns));
    const built = eligible.map(project => {
      const milestones = getProjectMilestones(project);
      const deadline = projectDeadlines.get(project.id) || null;
      const live = buildBars(milestones, null);
      const deadlineLeft = deadline ? dateOffsetPx(deadline, months, MONTH_W) : null;

      // Sort key: soonest thing on this project's timeline (deadline or any bar end).
      const dates = [
        ...(deadline ? [parseYMD(deadline)] : []),
        ...live.bars.map(b => parseYMD(b.end)),
      ].filter(n => !Number.isNaN(n));
      const earliest = dates.length > 0 ? Math.min(...dates) : Infinity;

      const msEvents = historyIndex.milestoneEvents.get(project.id) ?? [];
      let maxLanes = live.laneCount;
      if (history) {
        for (const at of planCheckpoints(project, msEvents, sliderStartMs)) {
          if (at > now) continue;
          maxLanes = Math.max(maxLanes, buildBars(milestonesAt(project, msEvents, at), at).laneCount);
        }
      }
      const lanesH = (n: number) => Math.max(BARS_MIN_H, n * (BAR_H + LANE_GAP) + LANE_GAP);
      return {
        project, milestones, live, deadline, deadlineLeft, earliest,
        liveBarsH: lanesH(live.laneCount), replayBarsH: lanesH(maxLanes),
        liveHasContent: live.bars.length > 0 || !!deadline,
      };
    });
    return built
      .filter(r => r.liveHasContent)
      .sort((a, b) => a.earliest - b.earliest);
  }, [projects, pipelineColumns, projectDeadlines, months, buildBars, historyIndex, history, sliderStartMs]);

  // The rows as of the slider's day (live when it's on today).
  const rows = useMemo(() => baseRows.map(r => {
    if (asOfMs == null) return { ...r, bars: r.live.bars, barsH: r.liveBarsH, presentAt: true, stageAt: null as string | null };
    const changes = historyIndex.statusChanges.get(r.project.id) ?? [];
    const stageAt = stageLabelAt(r.project, statusLabel(r.project.status_id), changes, asOfMs);
    const msEvents = historyIndex.milestoneEvents.get(r.project.id) ?? [];
    const { bars } = buildBars(milestonesAt(r.project, msEvents, asOfMs), asOfMs);
    return { ...r, bars, barsH: r.replayBarsH, presentAt: isPlanningOrExecutionLabel(stageAt), stageAt };
  }), [baseRows, asOfMs, historyIndex, statusLabel, buildBars]);

  // Chips per project: the events up to the slider's day, laid out so they
  // never overlap, plus the totals shown under the project's name.
  const activityByProject = useMemo(() => {
    const map = new Map<number, { clusters: EventCluster[]; meetings: number; tasks: number }>();
    for (const [projectId, events] of eventsByProject) {
      const shown = asOfMs == null ? events : events.filter(e => e.at <= asOfMs);
      if (shown.length === 0) continue;
      const meetings = shown.filter(e => e.kind === 'meeting').length;
      map.set(projectId, { clusters: clusterEvents(shown), meetings, tasks: shown.length - meetings });
    }
    return map;
  }, [eventsByProject, asOfMs]);

  // A chip's card belongs to the moment it was opened: close it when the
  // calendar is redrawn for another day.
  useEffect(() => { setCard(null); }, [asOfMs]);

  const jumpToday = useCallback(() => {
    if (todayLeft == null || !scrollRef.current) return;
    // Land with ~2 months of history already in view, so the recent past is
    // visible on first render without having to scroll left for it.
    scrollRef.current.scrollLeft = Math.max(0, LABEL_W + todayLeft - 2 * MONTH_W);
  }, [todayLeft]);

  useEffect(() => { jumpToday(); }, [jumpToday]);

  // While sliding, keep the chosen day in view: once its line reaches an
  // edge of the visible track, the timeline scrolls along with it.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || asOfLeft == null) return;
    const x = LABEL_W + asOfLeft;
    if (x < el.scrollLeft + LABEL_W + FOLLOW_MARGIN) el.scrollLeft = Math.max(0, asOfLeft - FOLLOW_MARGIN);
    else if (x > el.scrollLeft + el.clientWidth - FOLLOW_MARGIN) el.scrollLeft = x - el.clientWidth + FOLLOW_MARGIN;
  }, [asOfLeft]);

  // A card is anchored to where its chip was: scrolling the timeline closes it.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const close = () => setCard(null);
    el.addEventListener('scroll', close, { passive: true });
    window.addEventListener('resize', close);
    return () => { el.removeEventListener('scroll', close); window.removeEventListener('resize', close); };
  }, []);

  // Click-and-drag panning (grab hand), on top of the native scrollbar — the
  // timeline is much wider than the viewport, and dragging is the faster way
  // to browse it. Only suppresses the click that immediately follows an
  // actual drag, so bars/tasks/labels stay clickable for a plain click.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    const DRAG_THRESHOLD = 4;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
        moved = true;
        el.classList.add('is-dragging');
      }
      if (moved) {
        el.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      if (dragging && moved) {
        const suppressClick = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
        el.addEventListener('click', suppressClick, { capture: true, once: true });
        e.preventDefault();
      }
      dragging = false;
      moved = false;
      el.classList.remove('is-dragging');
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">{t('gerancemeta.emptyTitle')}</div>
        <div className="empty-state-desc">{t('gerancemeta.emptyDesc')}</div>
      </div>
    );
  }

  const isPast = asOf != null;

  return (
    <>
    <div className="cal-page">
      <div className="tl-legend cal-legend">
        <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-done" />{t('gerancemeta.milestoneStateDone')}</span>
        <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-active cal-bar-urg-normal" />{t('gerancemeta.milestoneUrgencyGreen')}</span>
        <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-active cal-bar-urg-soon" />{t('gerancemeta.milestoneUrgencyOrange')}</span>
        <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-active cal-bar-urg-overdue" />{t('gerancemeta.milestoneUrgencyRed')}</span>
        <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-pending" />{t('gerancemeta.milestoneStatePending')}</span>
        <span className="tl-legend-item"><MilestoneIcon size={12} className="icon-inline" />{t('gerancemeta.projectDeadline')}</span>
        <span className="tl-legend-sep" aria-hidden="true" />
        <span className="tl-legend-item">
          <span className="cal-chip cal-chip-legend" aria-hidden="true"><span className="cal-chip-seg is-meeting"><Users size={12} /></span></span>
          {t('gerancemeta.markMeeting')}
        </span>
        <span className="tl-legend-item">
          <span className="cal-chip cal-chip-legend" aria-hidden="true"><span className="cal-chip-seg is-task"><Check size={12} strokeWidth={3} /></span></span>
          {t('gerancemeta.markTaskDone')}
        </span>

        {/* Fixed-size control: nothing in it changes width while sliding,
            so the thumb never shifts under the pointer. */}
        <div className={`cal-timeslider${isPast ? ' is-past' : ''}`} title={t('gerancemeta.timeSliderHint')}>
          <History size={13} className="cal-timeslider-icon" aria-hidden="true" />
          <input
            className="cal-timeslider-range"
            type="range"
            min={0}
            max={sliderDays}
            step={1}
            value={sliderValue}
            style={{ '--fill': `${sliderDays > 0 ? (sliderValue / sliderDays) * 100 : 100}%` } as React.CSSProperties}
            onChange={(e) => {
              const v = Number(e.target.value);
              setAsOf(v >= sliderDays ? null : addDays(sliderStart, v));
            }}
            aria-label={t('gerancemeta.timeSliderHint')}
            aria-valuetext={asOf ? fmtShortDate(asOf) : t('planning.today')}
          />
          <span className="cal-timeslider-value">{asOf ? fmtShortDate(asOf) : t('planning.today')}</span>
          <button
            type="button"
            className="cal-timeslider-reset"
            onClick={() => { setAsOf(null); jumpToday(); }}
            disabled={!isPast}
            title={t('gerancemeta.timeSliderReset')}
            aria-label={t('gerancemeta.timeSliderReset')}
          >
            <X size={12} />
          </button>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={jumpToday}>
          {t('planning.jumpToday')}
        </button>
      </div>

      <div className="cal-scroll" ref={scrollRef}>
        <div className="cal-inner" style={{ width: LABEL_W + totalWidth }}>
          <div className="cal-header">
            <div className="cal-corner" style={{ width: LABEL_W }} />
            {months.map((m) => (
              <div
                key={monthKey(m)}
                className={`cal-month-head${todayMonthIdx >= 0 && monthKey(m) === monthKey(months[todayMonthIdx]) ? ' is-today' : ''}`}
                style={{ width: MONTH_W }}
              >
                {t(MONTH_KEYS[m.month])} {m.year}
              </div>
            ))}
            {asOfLeft != null && shownAsOf && (
              <div className="cal-asof-flag" style={{ left: LABEL_W + asOfLeft }}>{fmtShortDate(shownAsOf)}</div>
            )}
          </div>

          <div className={`cal-body${asOfMs != null ? ' is-replay' : ''}`}>
            {rows.map(({ project, bars, barsH, deadline, deadlineLeft, presentAt, stageAt }) => {
              const activity = activityByProject.get(project.id);
              const clusters = activity?.clusters ?? [];
              const openTasksByMilestone = new Map<string, Task[]>();
              for (const task of tasks) {
                if (task.project_id !== project.id || !task.milestone_id) continue;
                if (doneColumnIds.has(task.column_id)) continue;
                if (!openTasksByMilestone.has(task.milestone_id)) openTasksByMilestone.set(task.milestone_id, []);
                openTasksByMilestone.get(task.milestone_id)!.push(task);
              }
              const isExpanded = expandedRows.has(project.id);
              const blocked = project.is_blocked;
              const blockedSuffix = blocked ? ` — ${t('projectDetail.blockedBadge')}: ${project.blocked_reason || '—'}` : '';
              const sponsor = projectSponsor(project);
              const collaborators = projectCollaborators(project);
              const strategic = projectIsStrategic(project);
              const rcajoScore = projectRcajoScore(project);

              const activeBar = bars.find(b => b.state === 'active');
              const activeTasks = activeBar ? (openTasksByMilestone.get(activeBar.id) || []) : [];
              // Every bar currently showing its tasks strip: the active one when the
              // row is expanded, plus any done/pending bar toggled independently.
              const visibleGroups = bars
                .filter(b => b.state === 'active' ? isExpanded : expandedMilestones.has(`${project.id}:${b.id}`))
                .map(b => ({ bar: b, tasks: openTasksByMilestone.get(b.id) || [] }))
                .filter(g => g.tasks.length > 0);

              const absentTitle = !presentAt && shownAsOf
                ? (stageAt ? t('gerancemeta.absentAt', { date: fmtShortDate(shownAsOf), stage: stageAt }) : t('gerancemeta.notCreatedAt', { date: fmtShortDate(shownAsOf) }))
                : undefined;
              return (
                <div key={project.id} className="cal-row-group">
                  <div
                    className={`cal-row${blocked ? ' is-blocked' : ''}${!presentAt ? ' is-absent' : ''}`}
                    title={absentTitle ?? (blocked ? (project.blocked_reason || t('projectDetail.blockedBadge')) : undefined)}
                  >
                    <div className="cal-row-label" style={{ width: LABEL_W, paddingBottom: RAIL_H + 8 }}>
                      <div className="cal-row-label-main" onClick={() => goToProject(project.id)}>
                        <div className="cal-row-title" title={project.name}>
                          <span className="gerance-meta-card-code">{projectDisplayCode(project)} — </span>
                          {project.name}
                        </div>
                        {sponsor && <div className="cal-row-sponsor">{sponsor}</div>}
                        {isExpanded && (
                          <div className="cal-row-details">
                            {project.domain_label && <div className="cal-row-detail-line">{project.domain_label}</div>}
                            {collaborators && <div className="cal-row-detail-line">{collaborators}</div>}
                            <div className="cal-row-detail-line">
                              {t('gerancemeta.rcajoScoreLine')} {strategic ? (rcajoScore != null ? `${rcajoScore}/5` : t('gerancemeta.rcajoNoScore')) : t('gerancemeta.rcajoNotLinked')}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="cal-row-expand-btn"
                        title={t('gerancemeta.tasksTitle', { n: activeTasks.length })}
                        onClick={(e) => { e.stopPropagation(); toggleRow(project.id); }}
                      >
                        {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </button>
                      {/* The rail's band continues under the name, with the
                          project's totals: it reads as part of this row. */}
                      <div className="cal-rail-label" style={{ height: RAIL_H }} title={t('gerancemeta.railTotalsHint', { date: fmtShortDate(sliderStart) })}>
                        {activity && activity.meetings > 0 && (
                          <span className="cal-rail-sum is-meeting"><Users size={12} />{activity.meetings}</span>
                        )}
                        {activity && activity.tasks > 0 && (
                          <span className="cal-rail-sum is-task"><Check size={12} strokeWidth={3} />{activity.tasks}</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="cal-row-track"
                      style={{
                        width: totalWidth,
                        backgroundImage: TRACK_BG,
                        minHeight: barsH + RAIL_H,
                      }}
                    >
                      {bars.map(bar => {
                        const barTaskCount = openTasksByMilestone.get(bar.id)?.length ?? 0;
                        const milestoneKey = `${project.id}:${bar.id}`;
                        const isBarExpanded = bar.state === 'active' ? isExpanded : expandedMilestones.has(milestoneKey);
                        const barTop = LANE_GAP + bar.lane * (BAR_H + LANE_GAP);
                        return (
                          <div key={bar.id}>
                            <div
                              className={`cal-bar cal-bar-${bar.state}${bar.urgency ? ` cal-bar-urg-${bar.urgency}` : ''}`}
                              style={{ left: bar.left, width: bar.width, height: BAR_H, top: barTop }}
                              title={`${bar.name || t('milestones.untitled')} · ${bar.start || '—'} → ${bar.end}${blockedSuffix}`}
                              onClick={() => goToProject(project.id)}
                            >
                              <div className="cal-bar-pattern" />
                              <div className="cal-bar-body">
                                <span className="cal-bar-label">{bar.name || t('milestones.untitled')}</span>
                                {barTaskCount > 0 && bar.state !== 'active' && (
                                  <button
                                    type="button"
                                    className={`cal-bar-expand-btn${isBarExpanded ? ' active' : ''}`}
                                    title={t('gerancemeta.tasksTitle', { n: barTaskCount })}
                                    onClick={(e) => { e.stopPropagation(); toggleMilestone(milestoneKey); }}
                                  >
                                    {isBarExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                  </button>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="cal-bar-info-btn"
                              style={{ left: bar.left + bar.width, top: barTop }}
                              title={t('gerancemeta.milestoneInfoBtn')}
                              onClick={(e) => { e.stopPropagation(); setSelectedMilestone({ project, bar }); }}
                            >
                              <CircleAlert size={12} />
                            </button>
                          </div>
                        );
                      })}
                      {deadline && deadlineLeft != null && (
                        <div className="cal-deadline-marker" style={{ left: deadlineLeft, top: barsH / 2 }} title={`${t('gerancemeta.projectDeadline')}: ${deadline}${blockedSuffix}`}>
                          <MilestoneIcon size={14} />
                        </div>
                      )}

                      {/* Activity rail: what happened, on its day. */}
                      <div className="cal-rail" style={{ height: RAIL_H, backgroundImage: RAIL_BG }}>
                        {clusters.map(c => {
                          const nm = c.meetings.length;
                          const nt = c.tasks.length;
                          const isOpen = card?.projectId === project.id && card.cluster.x === c.x;
                          const label = [
                            nm > 0 ? t('gerancemeta.markMeetingsTitle', { n: nm }) : '',
                            nt > 0 ? t('gerancemeta.markTasksDoneTitle', { n: nt }) : '',
                          ].filter(Boolean).join(', ');
                          return (
                            <button
                              key={Math.round(c.x)}
                              type="button"
                              className={`cal-chip${isOpen ? ' is-open' : ''}`}
                              style={{ left: c.x - CHIP_ICON_OFFSET }}
                              aria-label={label}
                              onMouseEnter={(e) => openCard(project.id, c, e.currentTarget)}
                              onMouseLeave={scheduleClose}
                              onFocus={(e) => openCard(project.id, c, e.currentTarget)}
                              onBlur={scheduleClose}
                              onClick={(e) => {
                                e.stopPropagation();
                                // One task: straight to it. Anything else: the card lists them.
                                if (nm === 0 && nt === 1 && c.tasks[0].id != null) goToTask(c.tasks[0].id);
                                else openCard(project.id, c, e.currentTarget);
                              }}
                            >
                              {nm > 0 && (
                                <span className="cal-chip-seg is-meeting">
                                  <Users size={12} />
                                  {nm > 1 && <span className="cal-chip-n">{nm}</span>}
                                </span>
                              )}
                              {nt > 0 && (
                                <span className="cal-chip-seg is-task">
                                  <Check size={12} strokeWidth={3} />
                                  {nt > 1 && <span className="cal-chip-n">{nt}</span>}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {visibleGroups.length > 0 && (
                    <div className="cal-row-expand">
                      {visibleGroups.map(({ bar, tasks: groupTasks }) => (
                        <div key={bar.id} className="cal-expand-group" style={{ marginLeft: LABEL_W + bar.left }}>
                          {groupTasks.map(task => {
                            const taskBlocked = blockedColumnIds.has(task.column_id);
                            return (
                              <div
                                key={task.id}
                                className={`gerance-meta-task-row${taskBlocked ? ' is-blocked' : ''}`}
                                title={taskBlocked ? (task.blocked_reason || t('gerancemeta.taskBlocked')) : undefined}
                                onClick={() => goToTask(task.id)}
                              >
                                {taskBlocked
                                  ? <Ban size={12} className="icon-inline" style={{ color: 'var(--err)' }} />
                                  : <CheckSquare size={12} className="icon-inline" />}
                                <span className="gerance-meta-task-title">
                                  {task.title}
                                  {task.assignee_name && <span className="gerance-meta-task-meta"> – {task.assignee_name}</span>}
                                  {task.due_date && <span className="gerance-meta-task-meta"> – {fmtShortDate(task.due_date)}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {todayLeft != null && (
              <div className="cal-today-line" style={{ left: LABEL_W + todayLeft }} title={t('planning.today')} />
            )}
            {asOfLeft != null && (
              <div className="cal-asof-line" style={{ left: LABEL_W + asOfLeft }} />
            )}
          </div>
        </div>
      </div>
    </div>
    {card && <EventCard card={card} onEnter={cancelClose} onLeave={scheduleClose} t={t} />}
    {selectedMilestone && (
      <MilestoneDetailPanel
        project={selectedMilestone.project}
        milestoneId={selectedMilestone.bar.id}
        tasks={tasks}
        doneColumnIds={doneColumnIds}
        blockedColumnIds={blockedColumnIds}
        onClose={() => setSelectedMilestone(null)}
      />
    )}
    </>
  );
}
